import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { UpsertCouponWithPromoCodeDto } from 'views/Coupons/types';

import { Coupon, CouponConfiguration, PromoCode } from '../common/contracts';
import { EQueryKeys } from '../constants/enums';
import useAxios from '../hooks/useAxios';

import {
  ApiRoutes,
  EApiRoutes,
  fetchConfig,
  getApiRouteOrMock
} from './api.utils';

export default function useCoupons(
  currentPublisherId?: string | null,
  couponId?: string | null
) {
  const axios = useAxios();
  const queryClient = useQueryClient();

  const getCoupons = useQuery({
    ...fetchConfig.general,
    queryKey: [EQueryKeys.COUPONS, currentPublisherId],
    queryFn: async () => {
      const coupons = await axios.get(
        getApiRouteOrMock(EApiRoutes.COUPONS),
        {},
        currentPublisherId ? { 'x-publishers': currentPublisherId } : undefined
      );
      return coupons as Coupon[];
    },
    enabled: !!currentPublisherId
  });

  const getCouponsConfiguration = useQuery({
    ...fetchConfig.general,
    queryKey: [EQueryKeys.COUPONS_CONFIGURATION, currentPublisherId],
    queryFn: async () => {
      const coupons = await axios.get(
        getApiRouteOrMock(EApiRoutes.COUPONS_CONFIGURATION),
        {},
        currentPublisherId ? { 'x-publishers': currentPublisherId } : undefined
      );
      return coupons as CouponConfiguration;
    },
    enabled: !!currentPublisherId
  });

  const getSingleCoupon = useQuery({
    queryKey: [EQueryKeys.SINGLE_COUPON, couponId],
    queryFn: async () => {
      const coupon = await axios.get(
        `${ApiRoutes.COUPONS}/${couponId}`,
        {},
        currentPublisherId ? { 'x-publishers': currentPublisherId } : undefined
      );
      return coupon as Coupon;
    },
    enabled: !!couponId && !!currentPublisherId
  } as const);

  const updateCouponsConfiguration = useMutation({
    mutationFn: async (couponsConfiguration: CouponConfiguration) => {
      const response = await axios.put(
        getApiRouteOrMock(EApiRoutes.COUPONS_CONFIGURATION),
        couponsConfiguration,
        currentPublisherId ? { 'x-publishers': currentPublisherId } : undefined
      );
      return response.data;
    },
    onSuccess: async () =>
      queryClient.invalidateQueries({
        queryKey: [EQueryKeys.COUPONS_CONFIGURATION, currentPublisherId]
      })
  });

  const setCouponActive = useMutation(
    async ({ couponId, active }: { couponId: string; active: boolean }) => {
      const response = await axios.put(
        `${ApiRoutes.COUPONS}/${couponId}/setActivation`,
        { active },
        currentPublisherId ? { 'x-publishers': currentPublisherId } : undefined
      );
      return response.data;
    }
  );

  const upsertCoupon = useMutation(
    async (upsertCouponData: UpsertCouponWithPromoCodeDto) => {
      const response = await axios.post(
        getApiRouteOrMock(EApiRoutes.COUPONS),
        upsertCouponData,
        currentPublisherId ? { 'x-publishers': currentPublisherId } : undefined
      );
      return response.data;
    }
  );

  const isPromoCodeActive = useMutation(async (promoCodeName: string) => {
    return await axios.get(
      `${getApiRouteOrMock(EApiRoutes.PROMO_CODE)}/${promoCodeName}/isActive`,
      {},
      currentPublisherId ? { 'x-publishers': currentPublisherId } : undefined
    );
  });

  return {
    getCoupons,
    getSingleCoupon,
    upsertCoupon,
    setCouponActive,
    isPromoCodeActive,
    updateCouponsConfiguration,
    getCouponsConfiguration
  };
}
